import axios from 'axios';
import INotificationProps from '../dtos/INotificationProps';
// import { firebaseAuth } from '../config/firebase';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});
// TODO: Reimplementar autenticação com Firebase para garantir token atualizado em cada request
// // Garante token sempre fresco antes de cada request
// api.interceptors.request.use(async config => {
//   const currentUser = firebaseAuth.currentUser;
//   if (currentUser) {
//     const token = await currentUser.getIdToken();
//     config.headers = config.headers ?? {};
//     config.headers.authorization = `Bearer ${token}`;
//   }
//   return config;
// });

// Nomes de erro retornados pelo backend (campo `name` do corpo da resposta)
export const API_ERRORS = {
  PROFILE_INCOMPLETE: 'user/profile-incomplete',
  PROFILE_ALREADY_COMPLETED: 'user/profile-already-completed',
  SCHOOL_NOT_FOUND: 'school/not-found',
  TOURNAMENT_DEFAULT_SCHOOL: 'tournament/default-school-not-allowed',
  USER_NOT_ACTIVE: 'user/not-active',
  TOKEN_EXPIRED: 'auth/id-token-expired',
  UNSUPPORTED_MEDIA_TYPE: 'upload/unsupported-media-type',
  MEDIA_TOO_LARGE: 'upload/media-too-large',
} as const;

export const getApiErrorName = (error: any): string | undefined =>
  error?.response?.data?.name;

export const getApiErrorStatus = (error: any): number | undefined =>
  error?.response?.status;

/**
 * Handler acionado quando o backend responde 403 `user/profile-incomplete`.
 * É um callback registrado pelo AuthProvider (e não `window.location`) porque
 * uma navegação dura destruiria a SPA e reiniciaria o Firebase. O callback
 * altera o estado de autenticação e o roteamento segue declarativamente.
 */
let profileIncompleteHandler: (() => void) | null = null;

export const setProfileIncompleteHandler = (
  handler: (() => void) | null,
): void => {
  profileIncompleteHandler = handler;
};

/**
 * Rede de segurança global: qualquer rota bloqueada por perfil incompleto
 * avisa o AuthProvider, que redireciona para a tela de conclusão.
 *
 * Registrado no load do módulo, portanto SEMPRE antes do interceptor de
 * notificações (que é registrado em useEffect). Ele nunca desloga o usuário —
 * o único caminho de logout continua em hooks/auth.tsx, restrito a 401.
 *
 * ATENÇÃO: precisa sempre rejeitar. Se resolvesse, o branch de sucesso do
 * interceptor de notificações rodaria contra um valor que não é response.
 * Um futuro `api.interceptors.response.clear()` mataria esta proteção.
 */
api.interceptors.response.use(
  response => response,
  error => {
    if (
      getApiErrorStatus(error) === 403 &&
      getApiErrorName(error) === API_ERRORS.PROFILE_INCOMPLETE
    ) {
      profileIncompleteHandler?.();
    }

    return Promise.reject(error);
  },
);

// ID do interceptor ativo — evita empilhamento ao chamar setupNotificationInterceptor múltiplas vezes
let activeInterceptorId: number | null = null;

export const setupNotificationInterceptor = (
  addNotificationsCallback: (notifications: INotificationProps[]) => void,
): void => {
  // Remove interceptor anterior se existir (previne duplicação)
  if (activeInterceptorId !== null) {
    api.interceptors.response.eject(activeInterceptorId);
  }

  activeInterceptorId = api.interceptors.response.use(
    response => {
      if (response.data && Array.isArray(response.data.notifications)) {
        const formattedNotifications = response.data.notifications.map(
          (notification: Partial<INotificationProps>) => ({
            ...notification,
            id:
              notification.id ||
              `notif-${Date.now()}-${Math.random()
                .toString(36)
                .substring(2, 9)}`,
            created_at: notification.created_at || new Date(),
            updated_at: notification.updated_at || new Date(),
            read: notification.read || false,
          }),
        ) as INotificationProps[];

        if (formattedNotifications.length > 0) {
          addNotificationsCallback(formattedNotifications);
        }
      }
      return response;
    },
    error => {
      if (
        error.response &&
        error.response.data &&
        Array.isArray(error.response.data.notifications)
      ) {
        const formattedNotifications = error.response.data.notifications.map(
          (notification: Partial<INotificationProps>) => ({
            ...notification,
            id:
              notification.id ||
              `notif-${Date.now()}-${Math.random()
                .toString(36)
                .substring(2, 9)}`,
            created_at: notification.created_at || new Date(),
            updated_at: notification.updated_at || new Date(),
            read: notification.read || false,
          }),
        ) as INotificationProps[];

        if (formattedNotifications.length > 0) {
          addNotificationsCallback(formattedNotifications);
        }
      }
      return Promise.reject(error);
    },
  );
};

export default api;
